import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus,
    Receipt,
    ArrowUpFromLine,
    Banknote,
    Gift,
    Repeat,
    FileText,
    LineChart,
    Package,
    Coins,
    FolderLock,
    Inbox,
    Plane,
} from "lucide-react";

/**
 * Prominent Quick-Add menu — top-header trigger + dropdown of quick actions.
 *
 * The first item — "Upload to VAULT" — routes to the universal Inbox
 * (VAULT decides where the file belongs). Existing per-module quick
 * actions are kept below for users who already know where a record
 * should land.
 */
const FEATURED = {
    label: "Upload to VAULT",
    to: "/inbox",
    icon: Inbox,
    hint: "Any document — VAULT decides where it belongs.",
};

const ACTIONS = [
    { label: "Add expense", to: "/quick-entry", icon: Receipt },
    { label: "Add cash entry", to: "/cash-desk?tab=ledger&add=expense", icon: Coins },
    { label: "Add income", to: "/income", icon: ArrowUpFromLine },
    { label: "Record card payment", to: "/payments", icon: Banknote },
    { label: "Record cashback", to: "/refunds", icon: Gift },
    { label: "Add subscription", to: "/subscriptions", icon: Repeat },
    { label: "Upload statement", to: "/statements", icon: FileText },
    { label: "Add investment", to: "/investments", icon: LineChart },
    { label: "Add asset", to: "/assets", icon: Package },
    { label: "Add liability", to: "/bills", icon: Coins },
    { label: "Upload document", to: "/documents", icon: FolderLock },
    { label: "Add boarding pass", to: "/organise/travel/add", icon: Plane },
];

export default function QuickAddMenu({ canAdd = true }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        function onDoc(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (!canAdd) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-stone-900 text-white hover:bg-stone-700 transition-colors"
                data-testid="topbar-quick-add"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Plus size={14} strokeWidth={2.4} /> Quick Add
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-2 w-72 bg-white border border-stone-200 rounded-xl shadow-xl overflow-hidden z-50"
                    role="menu"
                    data-testid="quick-add-menu"
                >
                    <div className="px-4 py-2.5 border-b border-stone-100 text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
                        Quick Add
                    </div>
                    <button
                        onClick={() => { setOpen(false); navigate(FEATURED.to); }}
                        className="w-full flex items-start gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 via-amber-50 to-white border-b border-amber-100 hover:from-amber-100 transition-colors text-left"
                        data-testid="quick-add-upload-to-vault"
                    >
                        <span className="mt-0.5 w-8 h-8 rounded-full bg-stone-900 text-amber-400 flex items-center justify-center shrink-0">
                            <FEATURED.icon size={15} strokeWidth={2} />
                        </span>
                        <span className="flex-1">
                            <span className="block text-[13.5px] font-semibold text-stone-900">
                                {FEATURED.label}
                            </span>
                            <span className="block text-[11.5px] text-stone-600 mt-0.5">
                                {FEATURED.hint}
                            </span>
                        </span>
                    </button>
                    <div className="px-4 pt-2 pb-1 text-[10.5px] uppercase tracking-widest text-stone-400 font-semibold">
                        Or add directly
                    </div>
                    <ul className="py-1">
                        {ACTIONS.map(({ label, to, icon: Icon }) => (
                            <li key={to + label}>
                                <button
                                    onClick={() => { setOpen(false); navigate(to); }}
                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                                    data-testid={`quick-add-${label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                                >
                                    <Icon size={15} strokeWidth={1.7} className="text-stone-500" />
                                    <span className="flex-1 text-left">{label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
